import React, { useEffect, useState } from 'react';
import { Target, TrendingUp, DollarSign, Calendar, Sparkles, Info } from 'lucide-react';
import type { FinancialObjectives } from '../../types/businessPlan';
import type { SectorMetrics } from '../../types/essReference';

interface ObjectiveInputsProps {
  objectives: FinancialObjectives;
  onChange: (objectives: FinancialObjectives) => void;
  className?: string;
  sectorBenchmark?: SectorMetrics | null;
  projectCategory?: string;
  onApplySectorAverage?: (field: keyof FinancialObjectives) => void;
}

export const ObjectiveInputs: React.FC<ObjectiveInputsProps> = ({
  objectives,
  onChange,
  className = '',
  sectorBenchmark,
  projectCategory,
  onApplySectorAverage
}) => {
  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  const handleChange = (field: keyof FinancialObjectives, value: number) => {
    onChange({
      ...objectives,
      [field]: value
    });
  };

  const handleApplySectorValue = (field: keyof FinancialObjectives, value: number) => {
    handleChange(field, Math.round(value));
    onApplySectorAverage?.(field);
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString('fr-FR');
  };

  const getDeviationStatus = (userValue: number, avgValue: number) => {
    const deviation = ((userValue - avgValue) / avgValue) * 100;
    if (Math.abs(deviation) < 20) return { color: 'text-blue-600', label: 'Dans la moyenne' };
    if (deviation > 0) return { color: 'text-green-600', label: 'Au-dessus' };
    return { color: 'text-orange-600', label: 'En-dessous' };
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center space-x-2 mb-4">
        <Target className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Objectifs Financiers</h3>
      </div>

      {/* CA Target */}
      <div>
        <label htmlFor="ca-target" className="block text-sm font-medium text-gray-700 mb-2">
          <DollarSign className="w-4 h-4 inline mr-1" />
          Chiffre d'Affaires Cible (€)
        </label>
        <div className="relative">
          <input
            id="ca-target"
            type="number"
            value={objectives.ca_target}
            onChange={(e) => handleChange('ca_target', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="500000"
            min="0"
            step="1000"
          />
          {sectorBenchmark && sectorBenchmark.avg_revenue_year1 && (
            <button
              type="button"
              onClick={() => handleApplySectorValue('ca_target', sectorBenchmark.avg_revenue_year1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs rounded transition-colors"
              title="Appliquer la moyenne sectorielle"
            >
              <Sparkles className="w-3 h-3" />
              <span>Moyenne</span>
            </button>
          )}
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-gray-500">
            Objectif : {formatNumber(objectives.ca_target)} €
          </p>
          {sectorBenchmark && sectorBenchmark.avg_revenue_year1 && (
            <div className="flex items-center space-x-1">
              <span className="text-xs text-gray-500">
                Moy. secteur: {formatNumber(sectorBenchmark.avg_revenue_year1)} €
              </span>
              <span className={`text-xs font-medium ${getDeviationStatus(objectives.ca_target, sectorBenchmark.avg_revenue_year1).color}`}>
                ({getDeviationStatus(objectives.ca_target, sectorBenchmark.avg_revenue_year1).label})
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Marge Target */}
      <div>
        <label htmlFor="marge-target" className="block text-sm font-medium text-gray-700 mb-2">
          <TrendingUp className="w-4 h-4 inline mr-1" />
          Marge Nette Cible (%)
        </label>
        <div className="relative">
          <input
            id="marge-target"
            type="number"
            value={objectives.marge_target}
            onChange={(e) => handleChange('marge_target', parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="15"
            min="0"
            max="100"
            step="0.1"
          />
          {sectorBenchmark && sectorBenchmark.avg_ebitda_margin_pct && (
            <button
              type="button"
              onClick={() => handleApplySectorValue('marge_target', sectorBenchmark.avg_ebitda_margin_pct)}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs rounded transition-colors"
              title="Appliquer la moyenne sectorielle"
            >
              <Sparkles className="w-3 h-3" />
              <span>Moyenne</span>
            </button>
          )}
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-gray-500">
            Objectif : {objectives.marge_target}%
          </p>
          {sectorBenchmark && sectorBenchmark.avg_ebitda_margin_pct && (
            <div className="flex items-center space-x-1">
              <span className="text-xs text-gray-500">
                Moy. secteur: {sectorBenchmark.avg_ebitda_margin_pct.toFixed(1)}%
              </span>
              <span className={`text-xs font-medium ${getDeviationStatus(objectives.marge_target, sectorBenchmark.avg_ebitda_margin_pct).color}`}>
                ({getDeviationStatus(objectives.marge_target, sectorBenchmark.avg_ebitda_margin_pct).label})
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Break Even Target */}
      <div>
        <label htmlFor="break-even-target" className="block text-sm font-medium text-gray-700 mb-2">
          <Calendar className="w-4 h-4 inline mr-1" />
          Point Mort Cible (mois)
        </label>
        <input
          id="break-even-target"
          type="number"
          value={objectives.break_even_target}
          onChange={(e) => handleChange('break_even_target', parseInt(e.target.value) || 0)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="12"
          min="1"
          max="60"
        />
        <p className="text-xs text-gray-500 mt-1">
          Objectif : {objectives.break_even_target} mois
        </p>
      </div>

      {/* Investment Budget */}
      <div>
        <label htmlFor="investment-budget" className="block text-sm font-medium text-gray-700 mb-2">
          💰 Budget d'Investissement (€)
        </label>
        <div className="relative">
          <input
            id="investment-budget"
            type="number"
            value={objectives.investment_budget}
            onChange={(e) => handleChange('investment_budget', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="100000"
            min="0"
            step="1000"
          />
          {sectorBenchmark && sectorBenchmark.avg_capex && (
            <button
              type="button"
              onClick={() => handleApplySectorValue('investment_budget', sectorBenchmark.avg_capex)}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs rounded transition-colors"
              title="Appliquer la moyenne sectorielle"
            >
              <Sparkles className="w-3 h-3" />
              <span>Moyenne</span>
            </button>
          )}
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-gray-500">
            Budget : {formatNumber(objectives.investment_budget)} €
          </p>
          {sectorBenchmark && sectorBenchmark.avg_capex && (
            <div className="flex items-center space-x-1">
              <span className="text-xs text-gray-500">
                Moy. secteur: {formatNumber(sectorBenchmark.avg_capex)} €
              </span>
              <span className={`text-xs font-medium ${getDeviationStatus(objectives.investment_budget, sectorBenchmark.avg_capex).color}`}>
                ({getDeviationStatus(objectives.investment_budget, sectorBenchmark.avg_capex).label})
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Growth Rate Target */}
      <div>
        <label htmlFor="growth-rate" className="block text-sm font-medium text-gray-700 mb-2">
          📈 Taux de Croissance Annuel (%)
        </label>
        <input
          id="growth-rate"
          type="number"
          value={objectives.growth_rate_target}
          onChange={(e) => handleChange('growth_rate_target', parseFloat(e.target.value) || 0)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="10"
          min="0"
          max="100"
          step="0.1"
        />
        <p className="text-xs text-gray-500 mt-1">
          Croissance : {objectives.growth_rate_target}% par an
        </p>
      </div>

      {/* Summary Card */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Résumé des Objectifs</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-blue-700">CA Cible :</span>
            <span className="font-medium text-blue-900 ml-1">
              {formatNumber(objectives.ca_target)} €
            </span>
          </div>
          <div>
            <span className="text-blue-700">Marge :</span>
            <span className="font-medium text-blue-900 ml-1">
              {objectives.marge_target}%
            </span>
          </div>
          <div>
            <span className="text-blue-700">Point Mort :</span>
            <span className="font-medium text-blue-900 ml-1">
              {objectives.break_even_target} mois
            </span>
          </div>
          <div>
            <span className="text-blue-700">Croissance :</span>
            <span className="font-medium text-blue-900 ml-1">
              {objectives.growth_rate_target}%
            </span>
          </div>
        </div>
      </div>

      {sectorBenchmark && projectCategory && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <Info className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-green-800">
              <p className="font-medium mb-1">Données de référence disponibles</p>
              <p>
                Cliquez sur les boutons "Moyenne" pour pré-remplir avec les valeurs moyennes
                du secteur <strong>{projectCategory}</strong> basées sur des projets ESS similaires.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};